import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, MapPin, Loader2, Briefcase, Building2, Clock, DollarSign, X, ArrowRight, Mail, BellRing } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const WORK_MODES = [["", "Any work mode"], ["onsite", "On-site"], ["remote", "Remote"], ["hybrid", "Hybrid"]];
const MODE_LABEL = { onsite: "On-site", remote: "Remote", hybrid: "Hybrid" };
const EMPTY = { q: "", location: "", industry: "", min_salary: "", work_mode: "" };

function fmtSalary(j) {
  if (!j.salary_min && !j.salary_max) return null;
  const cur = j.salary_currency || "USD";
  return `${cur} ${Number(j.salary_min || 0).toLocaleString()}–${Number(j.salary_max || 0).toLocaleString()}`;
}

export default function JobsBoard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { id } = useParams();
  const [filters, setFilters] = useState(EMPTY);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [applying, setApplying] = useState(false);

  const search = useCallback(async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/jobs", { params });
      setJobs(r.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { search(EMPTY); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link: /jobs/:id opens the job detail (from email CTA)
  useEffect(() => {
    if (!id) return;
    api.get(`/jobs/${id}`).then((r) => setActive(r.data)).catch(() => {});
  }, [id]);

  const apply = async (job) => {
    if (!user) { nav("/auth?mode=register"); return; }
    if (user.role !== "candidate") { toast.error("Sign in as a candidate to apply."); return; }
    setApplying(true);
    try {
      await api.post("/candidate/apply", { job_id: job.id });
      toast.success("Application submitted!");
      setActive(null);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setApplying(false);
    }
  };

  const reset = () => { setFilters(EMPTY); search(EMPTY); };

  return (
    <div data-testid="jobs-board">
      {/* Hero */}
      <div className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-5 py-16">
          <div className="font-mono2 text-xs uppercase tracking-widest text-[#3b6bff] mb-3">Darriker · Open roles</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl">Browse jobs matched by score, not keywords.</h1>
          <p className="text-base text-[#9CA3AF] mt-4 max-w-xl">Every role is AI-analyzed. Build your profile and get a transparent 0–100% fit score on each posting.</p>
          {!user && (
            <button data-testid="jobs-hero-cta" onClick={() => nav("/auth?mode=register")} className="mt-7 bg-white text-[#0A0A0A] px-6 py-3 text-sm font-medium inline-flex items-center gap-2 hover:bg-[#3b6bff] hover:text-white transition-colors">
              Create your candidate profile <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 py-8">
        {/* Filters */}
        <div className="border border-[#0A0A0A] p-5 mb-6 grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <input data-testid="jb-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Title, skill, company" className="lg:col-span-2 border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          <input data-testid="jb-location" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Location" className="border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          <select data-testid="jb-work-mode" value={filters.work_mode} onChange={(e) => setFilters({ ...filters, work_mode: e.target.value })} className="border border-[#0A0A0A] px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
            {WORK_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input data-testid="jb-salary" type="number" value={filters.min_salary} onChange={(e) => setFilters({ ...filters, min_salary: e.target.value })} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Min salary" className="border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          <div className="flex gap-2">
            <button data-testid="jb-search-btn" onClick={() => search()} className="btn-ink px-4 py-2.5 text-sm font-medium flex-1 inline-flex items-center justify-center gap-2"><Search size={16} /> Search</button>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <span className="font-mono2 text-xs uppercase text-[#4B5563]">{loading ? "Searching…" : `${jobs.length} open role${jobs.length === 1 ? "" : "s"}`}</span>
          {(filters.q || filters.location || filters.industry || filters.min_salary || filters.work_mode) && (
            <button data-testid="jb-reset" onClick={reset} className="font-mono2 text-xs uppercase text-[#002FA7] hover:underline">Clear filters</button>
          )}
        </div>

        <SavedSearchBanner filters={filters} user={user} />

        {loading ? (
          <div className="font-mono2 text-sm text-[#9CA3AF] flex items-center gap-2 py-16 justify-center"><Loader2 size={18} className="animate-spin" /> Loading roles…</div>
        ) : jobs.length === 0 ? (
          <div className="border border-[#E5E7EB] p-16 text-center" data-testid="jb-empty">
            <Briefcase size={32} className="mx-auto text-[#9CA3AF] mb-3" />
            <div className="font-display font-bold text-lg">No roles match your filters</div>
            <p className="text-sm text-[#9CA3AF] mt-1">Try widening your search or clearing filters.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jb-results">
            {jobs.map((j) => (
              <button key={j.id} data-testid={`jb-card-${j.id}`} onClick={() => setActive(j)} className="text-left border border-[#E5E7EB] p-5 hard-card hover:border-[#002FA7] transition-colors flex flex-col">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {j.work_mode && <span className="font-mono2 text-[10px] uppercase bg-[#002FA7] text-white px-1.5 py-0.5">{MODE_LABEL[j.work_mode] || j.work_mode}</span>}
                  {j.employment_type && <span className="font-mono2 text-[10px] uppercase border border-[#0A0A0A] px-1.5 py-0.5">{j.employment_type}</span>}
                </div>
                <div className="font-display font-bold text-lg leading-tight">{j.title}</div>
                <div className="text-sm text-[#4B5563] flex items-center gap-1.5 mt-1"><Building2 size={13} /> {j.company}</div>
                <div className="text-xs text-[#9CA3AF] flex items-center gap-1.5 mt-0.5"><MapPin size={12} /> {j.location}{j.industry ? ` · ${j.industry}` : ""}</div>
                {fmtSalary(j) && <div className="font-mono2 text-xs text-[#002FA7] mt-2 flex items-center gap-1"><DollarSign size={12} /> {fmtSalary(j)}</div>}
                {j.poster_logo && (
                  <div className="mt-2 flex items-center gap-1.5" data-testid="jb-poster">
                    <img src={j.poster_logo} alt={j.poster_name} className="w-4 h-4 object-contain rounded-sm" />
                    <span className="font-mono2 text-[10px] uppercase text-[#9CA3AF]">via {j.poster_name}</span>
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-1.5 flex-1">
                  {(j.required_skills || []).slice(0, 4).map((s) => <span key={s} className="font-mono2 text-xs border border-[#E5E7EB] px-2 py-0.5">{s}</span>)}
                </div>
                <span className="mt-4 font-mono2 text-xs uppercase text-[#002FA7] inline-flex items-center gap-1">View details <ArrowRight size={13} /></span>
              </button>
            ))}
          </div>
        )}
      </div>

      {active && <JobDetail job={active} onClose={() => { setActive(null); if (id) nav("/jobs"); }} onApply={apply} applying={applying} user={user} />}
    </div>
  );
}

function SavedSearchBanner({ filters, user }) {
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Reset the "subscribed" confirmation when the criteria change.
  useEffect(() => { setDone(false); }, [filters.q, filters.location, filters.work_mode, filters.min_salary]);

  const activeCrit = [
    filters.q && `\u201c${filters.q}\u201d`,
    filters.work_mode && ({ onsite: "On-site", remote: "Remote", hybrid: "Hybrid" }[filters.work_mode]),
    filters.location && `in ${filters.location}`,
    filters.min_salary && `min $${Number(filters.min_salary).toLocaleString()}`,
  ].filter(Boolean).join(" · ");

  const subscribe = async () => {
    if (!email.trim() || !email.includes("@")) return toast.error("Enter a valid email");
    setSaving(true);
    try {
      const r = await api.post("/job-alerts/subscribe", {
        email: email.trim(),
        q: filters.q || undefined,
        location: filters.location || undefined,
        work_mode: filters.work_mode || undefined,
        min_salary: filters.min_salary ? Number(filters.min_salary) : undefined,
      });
      setDone(true);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div data-testid="saved-search-done" className="border-2 border-[#10B981] bg-[#10B981]/5 p-5 mb-6 flex items-center gap-3">
        <BellRing size={20} className="text-[#10B981]" />
        <div className="text-sm">
          <span className="font-display font-bold">You're subscribed.</span>
          <span className="text-[#4B5563]"> We'll email you when a new role matches{activeCrit ? <> — <b>{activeCrit}</b></> : ""}.</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="saved-search-banner" className="border-2 border-[#0A0A0A] p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex items-start gap-3 flex-1">
        <BellRing size={22} className="text-[#002FA7] mt-0.5 shrink-0" />
        <div>
          <div className="font-display font-bold">Get emailed when a matching role is posted</div>
          <div className="text-xs text-[#4B5563] mt-0.5">
            {activeCrit ? <>We'll alert you for: <b>{activeCrit}</b></> : "Set your filters above, then subscribe to be alerted for new roles."}
          </div>
        </div>
      </div>
      <div className="flex gap-2 w-full sm:w-auto">
        <div className="relative flex-1 sm:w-56">
          <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            data-testid="saved-search-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && subscribe()}
            placeholder="you@email.com"
            className="w-full border border-[#0A0A0A] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          />
        </div>
        <button data-testid="saved-search-subscribe" onClick={subscribe} disabled={saving} className="btn-ink px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50 whitespace-nowrap">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <BellRing size={15} />} Alert me
        </button>
      </div>
    </div>
  );
}

function JobDetail({ job, onClose, onApply, applying, user }) {
  const salary = fmtSalary(job);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="jb-detail" onClick={onClose}>
      <div className="bg-white border-2 border-[#0A0A0A] w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#0A0A0A] text-white p-6 relative">
          <button data-testid="jb-detail-close" onClick={onClose} className="absolute top-4 right-4 text-[#9CA3AF] hover:text-white"><X size={22} /></button>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {job.work_mode && <span className="font-mono2 text-[10px] uppercase bg-[#3b6bff] text-white px-1.5 py-0.5">{MODE_LABEL[job.work_mode] || job.work_mode}</span>}
            {job.employment_type && <span className="font-mono2 text-[10px] uppercase border border-white/30 px-1.5 py-0.5">{job.employment_type}</span>}
          </div>
          <h2 className="font-display font-black text-2xl tracking-tighter">{job.title}</h2>
          <div className="text-sm text-[#9CA3AF] flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
            <span className="flex items-center gap-1.5"><Building2 size={14} /> {job.company}</span>
            <span className="flex items-center gap-1.5"><MapPin size={14} /> {job.location}</span>
            {salary && <span className="flex items-center gap-1.5"><DollarSign size={14} /> {salary}</span>}
          </div>
          {job.poster_logo && (
            <div className="mt-3 inline-flex items-center gap-2 bg-white/10 px-2.5 py-1" data-testid="jb-detail-poster">
              <img src={job.poster_logo} alt={job.poster_name} className="w-5 h-5 object-contain rounded-sm bg-white" />
              <span className="font-mono2 text-[10px] uppercase text-[#9CA3AF]">Posted via {job.poster_name}</span>
            </div>
          )}
        </div>
        <div className="p-6 space-y-5">
          {job.about_employer && (
            <div>
              <div className="font-mono2 text-xs uppercase text-[#002FA7] mb-1.5">About the employer</div>
              <p className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-line">{job.about_employer}</p>
            </div>
          )}
          <div>
            <div className="font-mono2 text-xs uppercase text-[#002FA7] mb-1.5">Role description</div>
            <p className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-line">{job.description}</p>
          </div>
          {(job.required_skills || []).length > 0 && (
            <div>
              <div className="font-mono2 text-xs uppercase text-[#002FA7] mb-1.5">Key skills</div>
              <div className="flex flex-wrap gap-1.5">
                {job.required_skills.map((s) => <span key={s} className="font-mono2 text-xs border border-[#0A0A0A] px-2 py-0.5">{s}</span>)}
              </div>
            </div>
          )}
          <div className="pt-2 flex gap-3">
            <button data-testid="jb-apply-btn" onClick={() => onApply(job)} disabled={applying} className="btn-ink px-6 py-3 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
              {applying ? <Loader2 size={16} className="animate-spin" /> : <Briefcase size={16} />}
              {!user ? "Sign up to apply" : user.role !== "candidate" ? "Candidates only" : "Apply now"}
            </button>
            <button data-testid="jb-detail-cancel" onClick={onClose} className="btn-outline px-6 py-3 text-sm font-medium">Close</button>
          </div>
          {!user && <p className="text-xs text-[#9CA3AF] flex items-center gap-1.5"><Clock size={12} /> Create a free candidate profile to apply and get AI match scores.</p>}
        </div>
      </div>
    </div>
  );
}
